import type { EvalRunListItem } from "@api/app-api";
import type { EvalRunData } from "@global/types/ai-evals";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Tab, Tabs, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../../api";
import { type CheckFilters, CheckDetail, Checks } from "./checks";
import { Overview } from "./overview";
import { RunDetail, Runs } from "./runs";
import { pct, type RunKey } from "./scoring";

type View = { tab: "overview" | "checks" | "runs"; checkId?: string; run?: RunKey };

const keyOf = (run: EvalRunListItem) => `${run.kind}/${run.id}`;

function runLabel(run: EvalRunListItem): string {
  const when = run.createdAt.slice(0, 16).replace("T", " ");
  const name = run.kind === "baselines" ? `Baseline ${run.label ?? run.id}` : run.label ? `${run.label} (run ${run.id})` : `Run ${run.id}`;
  const version = run.catalogVersion ? ` · checks v${run.catalogVersion}` : "";
  return `${name} · ${run.architecture}${version} · ${when} · interview ${pct(run.interviewScore)} · case ${pct(run.caseScore)}`;
}

/** Eval runs and baselines from the stage's results bucket; one run is shown, optionally against another as baseline. */
export const AiEvalsPage: React.FC = () => {
  const [runs, setRuns] = useState<EvalRunListItem[]>([]);
  const [selected, setSelected] = useState("");
  const [compare, setCompare] = useState("");
  const [data, setData] = useState<EvalRunData | null>(null);
  const [baseline, setBaseline] = useState<EvalRunData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ tab: "overview" });
  const [filters, setFilters] = useState<CheckFilters>({ scope: "all", group: "all", verdict: "all", q: "" });

  const loadRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { runs: list } = await apiClient.getEvalRuns();
      setRuns(list);
      // Default: the newest run against the newest baseline.
      const latest = list.find((r) => r.kind === "runs") ?? list[0];
      const newestBaseline = list.find((r) => r.kind === "baselines" && r !== latest);
      setSelected((current) => current || (latest ? keyOf(latest) : ""));
      setCompare((current) => current || (newestBaseline ? keyOf(newestBaseline) : ""));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load eval runs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRuns();
  }, [loadRuns]);

  const load = useCallback(
    async (key: string) => {
      const run = runs.find((r) => keyOf(r) === key);
      return run ? apiClient.getEvalRun(run) : null;
    },
    [runs],
  );

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([load(selected), compare && compare !== selected ? load(compare) : Promise.resolve(null)])
      .then(([run, base]) => {
        if (cancelled) return;
        setData(run);
        setBaseline(base);
        setView((v) => ({ tab: v.tab }));
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : "Failed to load the run"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [selected, compare, load]);

  const openRun = (run: RunKey) => setView({ tab: "runs", run: { scenarioId: run.scenarioId, run: run.run } });
  const openCheck = (checkId: string) => setView({ tab: "checks", checkId });
  const openGroup = (group: string) => {
    setFilters({ scope: "all", group, verdict: "all", q: "" });
    setView({ tab: "checks" });
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            AI Evals
          </Typography>
          <Typography color="text.secondary">
            Agent eval runs against this environment, graded against plans/EVAL-CHECKS.md. Start a run in GitHub: Actions → AI evals.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={() => void loadRuns()} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!loading && runs.length === 0 && !error && <Alert severity="info">No eval runs yet.</Alert>}

      {runs.length > 0 && (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 420, flex: 1 }}>
            <InputLabel>Run</InputLabel>
            <Select label="Run" value={selected} onChange={(e) => setSelected(e.target.value)}>
              {runs.map((r) => (
                <MenuItem key={keyOf(r)} value={keyOf(r)}>
                  {runLabel(r)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 420, flex: 1 }}>
            <InputLabel>Compare with</InputLabel>
            <Select label="Compare with" value={compare} onChange={(e) => setCompare(e.target.value)}>
              <MenuItem value="">No baseline</MenuItem>
              {runs
                .filter((r) => keyOf(r) !== selected)
                .map((r) => (
                  <MenuItem key={keyOf(r)} value={keyOf(r)}>
                    {runLabel(r)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && data && (
        <>
          <Tabs value={view.tab} onChange={(_e, tab: View["tab"]) => setView({ tab })} sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tab value="overview" label="Overview" />
            <Tab value="checks" label="Checks" />
            <Tab value="runs" label="Runs" />
          </Tabs>
          {view.tab === "overview" && <Overview data={data} baseline={baseline} onOpenGroup={openGroup} onOpenCheck={openCheck} onOpenRun={openRun} />}
          {view.tab === "checks" &&
            (view.checkId ? (
              <CheckDetail id={view.checkId} data={data} baseline={baseline} onBack={() => setView({ tab: "checks" })} onOpenRun={openRun} />
            ) : (
              <Checks data={data} baseline={baseline} filters={filters} onFilters={setFilters} onOpenCheck={openCheck} />
            ))}
          {view.tab === "runs" &&
            (view.run ? (
              <RunDetail
                key={`${view.run.scenarioId}-${view.run.run}`}
                runKey={view.run}
                data={data}
                baseline={baseline}
                onBack={() => setView({ tab: "runs" })}
                onOpenRun={openRun}
                onOpenCheck={openCheck}
              />
            ) : (
              <Runs data={data} baseline={baseline} onOpenRun={openRun} />
            ))}
        </>
      )}
    </Box>
  );
};
